import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Download,
  ExternalLink,
  FileStack,
  Globe,
  Loader2,
  PenTool,
  Plus,
  Search,
  ShieldAlert,
  UploadCloud,
} from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { formatDate } from "@/lib/i18n";

interface AgencyBuilderWorkspacePageSummary {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published" | "archived";
  publishedUrl: string | null;
}

interface AgencyBuilderWorkspaceSiteSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  siteType: string;
  homePageTitle: string | null;
  reviewState: "live" | "ready_for_review" | "changes_pending_review" | "empty";
  health: "healthy" | "attention" | "critical";
  healthNotes: string[];
  primaryPublishedUrl: string | null;
  updatedAt: string;
  counts: {
    pageCount: number;
    publishedPageCount: number;
    draftPageCount: number;
  };
  pages: AgencyBuilderWorkspacePageSummary[];
}

interface AgencyBuilderWorkspaceTenantSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  complianceTier: string;
  health: "healthy" | "attention" | "critical";
  reviewState: "live" | "ready_for_review" | "changes_pending_review" | "empty";
  healthNotes: string[];
  lastUpdatedAt: string | null;
  counts: {
    siteCount: number;
    pageCount: number;
    publishedPageCount: number;
    draftPageCount: number;
    standalonePageCount: number;
  };
  sites: AgencyBuilderWorkspaceSiteSummary[];
}

interface AgencyBuilderWorkspaceResponse {
  data: {
    metrics: {
      tenantCount: number;
      activeTenantCount: number;
      siteCount: number;
      pageCount: number;
      publishedPageCount: number;
      draftPageCount: number;
      reviewQueueCount: number;
      attentionTenantCount: number;
    };
    tenants: AgencyBuilderWorkspaceTenantSummary[];
  };
}

interface AgencyBulkPublishResponse {
  data: {
    results: Array<{
      tenantId: string;
      siteId: string;
      status: "queued" | "already_publishing" | "not_found" | "site_archived" | "no_pages" | "error";
      siteSlug: string | null;
      jobId: string | null;
      totalPages: number;
      message: string | null;
    }>;
  };
}

const HEALTH_TONE: Record<
  AgencyBuilderWorkspaceTenantSummary["health"],
  string
> = {
  healthy: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  attention: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  critical: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
};

const REVIEW_LABEL: Record<AgencyBuilderWorkspaceTenantSummary["reviewState"], string> = {
  live: "Live",
  ready_for_review: "Ready for review",
  changes_pending_review: "Pending review",
  empty: "Setup needed",
};

export default function AgencyDashboardPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const orgId = session?.activeOrganizationId ?? null;
  const [workspace, setWorkspace] = useState<AgencyBuilderWorkspaceResponse["data"] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "attention" | "ready" | "live">("all");
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [bulkPublishState, setBulkPublishState] = useState<"idle" | "submitting">("idle");
  const [bulkPublishMessage, setBulkPublishMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setWorkspace(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let isCancelled = false;

    async function loadWorkspace() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiFetch<AgencyBuilderWorkspaceResponse>(`/v1/platform/agency/builder-workspace/${orgId}`);
        if (!isCancelled) {
          setWorkspace(response.data);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setWorkspace(null);
          setError(loadError instanceof ApiError ? loadError.message : "Failed to load the agency builder workspace.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadWorkspace();

    return () => {
      isCancelled = true;
    };
  }, [orgId]);

  const topSites = useMemo(() => {
    const prioritizedSites = (workspace?.tenants ?? [])
      .flatMap((tenant) =>
        tenant.sites.map((site) => ({
          tenantId: tenant.id,
          tenantName: tenant.name,
          ...site,
        })),
      )
      .sort((left, right) => {
        const leftNeedsAttention = left.health !== "healthy" || left.reviewState !== "live";
        const rightNeedsAttention = right.health !== "healthy" || right.reviewState !== "live";
        if (leftNeedsAttention !== rightNeedsAttention) {
          return leftNeedsAttention ? -1 : 1;
        }

        const leftReviewWeight =
          left.reviewState === "empty" ? 0 : left.reviewState === "changes_pending_review" ? 1 : left.reviewState === "ready_for_review" ? 2 : 3;
        const rightReviewWeight =
          right.reviewState === "empty" ? 0 : right.reviewState === "changes_pending_review" ? 1 : right.reviewState === "ready_for_review" ? 2 : 3;
        if (leftReviewWeight !== rightReviewWeight) {
          return leftReviewWeight - rightReviewWeight;
        }

        const leftWeight = left.health === "critical" ? 0 : left.health === "attention" ? 1 : 2;
        const rightWeight = right.health === "critical" ? 0 : right.health === "attention" ? 1 : 2;
        if (leftWeight !== rightWeight) {
          return leftWeight - rightWeight;
        }

        return right.updatedAt.localeCompare(left.updatedAt);
      });

    const queue = prioritizedSites.filter((site) => site.health !== "healthy" || site.reviewState !== "live");
    return (queue.length > 0 ? queue : prioritizedSites)
      .sort((left, right) => {
        const leftWeight = left.health === "critical" ? 0 : left.health === "attention" ? 1 : 2;
        const rightWeight = right.health === "critical" ? 0 : right.health === "attention" ? 1 : 2;
        return leftWeight - rightWeight;
      })
      .slice(0, 4);
  }, [workspace]);

  const siteCards = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (workspace?.tenants ?? [])
      .flatMap((tenant) => tenant.sites.map((site) => ({
        tenantId: tenant.id,
        tenantName: tenant.name,
        tenantSlug: tenant.slug,
        tenantHealth: tenant.health,
        lastUpdatedAt: tenant.lastUpdatedAt,
        ...site,
      })))
      .filter((site) => {
        if (filter === "attention" && site.health === "healthy") return false;
        if (filter === "ready" && site.reviewState === "live") return false;
        if (filter === "live" && site.reviewState !== "live") return false;
        if (!normalizedQuery) return true;
        const haystack = [
          site.name,
          site.slug,
          site.tenantName,
          site.homePageTitle ?? "",
          site.siteType,
        ].join(" ").toLowerCase();
        return haystack.includes(normalizedQuery);
      })
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  }, [filter, query, workspace]);

  const selectedTargets = useMemo(() => {
    const selected = new Set(selectedSiteIds);
    return siteCards
      .filter((site) => selected.has(site.id))
      .map((site) => ({ tenantId: site.tenantId, siteId: site.id }));
  }, [selectedSiteIds, siteCards]);

  const allVisibleSelected = siteCards.length > 0 && siteCards.every((site) => selectedSiteIds.includes(site.id));

  const handleToggleVisibleSites = () => {
    if (allVisibleSelected) {
      setSelectedSiteIds((current) => current.filter((siteId) => !siteCards.some((site) => site.id === siteId)));
      return;
    }

    setSelectedSiteIds((current) => Array.from(new Set([...current, ...siteCards.map((site) => site.id)])));
  };

  const handleToggleSite = (siteId: string) => {
    setSelectedSiteIds((current) => (
      current.includes(siteId)
        ? current.filter((existingId) => existingId !== siteId)
        : [...current, siteId]
    ));
  };

  const handleExportCsv = () => {
    const rows = [
      ["Tenant", "Site", "Slug", "Status", "Review State", "Health", "Health Score", "Pages", "Published Pages", "Draft Pages", "Updated At", "Primary URL"],
      ...siteCards.map((site) => [
        site.tenantName,
        site.name,
        site.slug,
        site.status,
        REVIEW_LABEL[site.reviewState],
        site.health,
        String(computeHealthScore(site)),
        String(site.counts.pageCount),
        String(site.counts.publishedPageCount),
        String(site.counts.draftPageCount),
        site.updatedAt,
        site.primaryPublishedUrl ?? "",
      ]),
    ];
    const csv = rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "agency-sites.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkPublish = async () => {
    if (!orgId || selectedTargets.length === 0) {
      return;
    }

    setBulkPublishState("submitting");
    setBulkPublishMessage(null);

    try {
      const response = await apiFetch<AgencyBulkPublishResponse>(`/v1/platform/agency/builder-workspace/${orgId}/publish`, {
        method: "POST",
        body: JSON.stringify({ siteTargets: selectedTargets }),
      });
      const results = response.data.results;
      const queued = results.filter((result) => result.status === "queued");
      const blocked = results.filter((result) => result.status !== "queued");
      setBulkPublishMessage(
        blocked.length === 0
          ? `Queued ${queued.length} site publish job${queued.length === 1 ? "" : "s"} for live publish.`
          : `Queued ${queued.length} site publish job${queued.length === 1 ? "" : "s"}; ${blocked.length} site${blocked.length === 1 ? "" : "s"} were skipped.`,
      );
    } catch (loadError) {
      setBulkPublishMessage(loadError instanceof ApiError ? loadError.message : "Failed to queue the selected site publishes.");
    } finally {
      setBulkPublishState("idle");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-2xl" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-80" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((item) => (
            <SkeletonCard key={item} className="h-28 rounded-3xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <WorkspaceEmptyState
        title="Agency workspace needs an active organization"
        description="Set an active organization in your authenticated session before loading org-scoped client sites and pages."
      />
    );
  }

  if (error) {
    return (
      <WorkspaceEmptyState
        title="Agency builder workspace could not be loaded"
        description={error}
      />
    );
  }

  const metrics = workspace?.metrics;
  const tenants = workspace?.tenants ?? [];

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.05),rgba(15,23,42,0))] p-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-300">
              <PenTool className="h-3.5 w-3.5" />
              Agency Builder Workspace
            </span>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Manage client sites and review queues without pretending the missing pieces exist.</h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              This view uses the current organization&apos;s real sub-accounts plus each client tenant&apos;s existing sites and pages. Search, filter, CSV export, and bulk publish all run on the mounted agency workspace path instead of a parallel admin-only dashboard.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => navigate("/agency/activate")}
              className="rounded-2xl border border-border bg-background px-4 py-3 text-left text-sm text-foreground transition hover:bg-accent"
            >
              <span className="mb-1 flex items-center gap-2 font-medium">
                <Plus className="h-4 w-4" />
                Add Site
              </span>
              Activate a client tenant, then move into their site workspace.
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              className="rounded-2xl border border-border bg-background px-4 py-3 text-left text-sm text-foreground transition hover:bg-accent"
            >
              <span className="mb-1 flex items-center gap-2 font-medium">
                <Download className="h-4 w-4" />
                Export CSV
              </span>
              Export the currently visible site grid for ops review or handoff.
            </button>
            <button
              type="button"
              onClick={() => void handleBulkPublish()}
              disabled={selectedTargets.length === 0 || bulkPublishState === "submitting"}
              className="rounded-2xl border border-border bg-background px-4 py-3 text-left text-sm text-foreground transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="mb-1 flex items-center gap-2 font-medium">
                {bulkPublishState === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                Bulk Publish
              </span>
              Queue live publish for the currently selected sites.
            </button>
          </div>
        </div>
      </section>

      {bulkPublishMessage ? (
        <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-foreground">
          {bulkPublishMessage}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Clients" value={String(metrics?.tenantCount ?? 0)} detail={`${metrics?.activeTenantCount ?? 0} active`} icon={Building2} />
        <MetricCard label="Sites" value={String(metrics?.siteCount ?? 0)} detail={`${metrics?.reviewQueueCount ?? 0} in review queue`} icon={Globe} />
        <MetricCard label="Pages" value={String(metrics?.pageCount ?? 0)} detail={`${metrics?.publishedPageCount ?? 0} published`} icon={FileStack} />
        <MetricCard label="Attention Needed" value={String(metrics?.attentionTenantCount ?? 0)} detail={`${metrics?.draftPageCount ?? 0} draft pages`} icon={ShieldAlert} />
      </section>

      <section className="rounded-[28px] border border-border bg-card p-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Priority queue</h2>
            <p className="text-sm text-muted-foreground">
              Focus the next agency action on the mounted sites that need review, setup, or health intervention first.
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            {metrics?.reviewQueueCount ?? 0} sites waiting on review state changes
          </p>
        </div>

        {topSites.length === 0 ? (
          <div className="mt-5 rounded-[24px] border border-dashed border-border bg-background px-6 py-10 text-center text-sm text-muted-foreground">
            No client sites are available in this workspace yet.
          </div>
        ) : (
          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            {topSites.map((site) => (
              <article key={site.id} className="stagger-item rounded-[24px] border border-border bg-background p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">{site.tenantName}</p>
                    <h3 className="text-lg font-semibold text-foreground">{site.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {site.counts.pageCount} pages · updated {formatRelativeDate(site.updatedAt)}
                    </p>
                  </div>
                  <div className="space-y-2 text-right">
                    <StatusChip tone={site.health}>{site.health}</StatusChip>
                    <StatusChip tone={site.reviewState === "live" ? "healthy" : site.reviewState === "empty" ? "critical" : "attention"}>
                      {REVIEW_LABEL[site.reviewState]}
                    </StatusChip>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <SummaryPill label="Health" value={computeHealthScore(site)} />
                  <SummaryPill label="Live" value={site.counts.publishedPageCount} />
                  <SummaryPill label="Drafts" value={site.counts.draftPageCount} />
                </div>

                <div className="mt-4 rounded-2xl border border-border bg-card px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Next attention point</p>
                  <p className="mt-1 text-sm font-medium text-foreground">
                    {site.healthNotes[0] ?? "Review queued changes before the next publish window."}
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/agency/manage/${site.tenantId}`)}
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                  >
                    Open client workspace
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  {site.primaryPublishedUrl ? (
                    <a
                      href={site.primaryPublishedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                    >
                      View live
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[28px] border border-border bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Client sites</h2>
            <p className="text-sm text-muted-foreground">Search, filter, score, and bulk-publish the real sites inside this agency workspace.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search sites or clients"
                className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none"
              />
            </label>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value as typeof filter)}
              className="h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground outline-none"
            >
              <option value="all">All sites</option>
              <option value="attention">Needs attention</option>
              <option value="ready">Ready or pending review</option>
              <option value="live">Live only</option>
            </select>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
          <label className="inline-flex items-center gap-3">
            <input type="checkbox" checked={allVisibleSelected} onChange={handleToggleVisibleSites} />
            <span>Select visible sites</span>
          </label>
          <span>{siteCards.length} visible · {selectedTargets.length} selected</span>
        </div>

        {siteCards.length === 0 ? (
          <div className="mt-5 rounded-[24px] border border-dashed border-border bg-background px-6 py-10 text-center text-sm text-muted-foreground">
            No sites match the current filters.
          </div>
        ) : (
          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            {siteCards.map((site) => (
              <article key={site.id} className="stagger-item rounded-[28px] border border-border bg-background p-5">
                <div className="flex items-start justify-between gap-4">
                  <label className="inline-flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedSiteIds.includes(site.id)}
                      onChange={() => handleToggleSite(site.id)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm text-muted-foreground">{site.tenantName}</p>
                      <h3 className="text-lg font-semibold text-foreground">{site.name}</h3>
                      <p className="text-sm text-muted-foreground">/{site.slug}</p>
                    </div>
                  </label>
                  <div className="space-y-2 text-right">
                    <StatusChip tone={site.health}>{REVIEW_LABEL[site.reviewState]}</StatusChip>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      Health {computeHealthScore(site)}
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-4">
                  <SummaryPill label="Pages" value={site.counts.pageCount} />
                  <SummaryPill label="Live" value={site.counts.publishedPageCount} />
                  <SummaryPill label="Drafts" value={site.counts.draftPageCount} />
                  <SummaryPill label="Updated" value={formatRelativeDate(site.updatedAt)} />
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <InfoRow label="Domain" value={site.primaryPublishedUrl ? "Live domain connected" : "No live domain yet"} />
                  <InfoRow label="Home" value={site.homePageTitle ?? "No home page"} />
                </div>

                {site.healthNotes.length > 0 ? (
                  <div className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${HEALTH_TONE[site.health]}`}>
                    <div className="space-y-1">
                      {site.healthNotes.map((note) => (
                        <p key={note}>{note}</p>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/agency/manage/${site.tenantId}`)}
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                  >
                    Review client workspace
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  {site.primaryPublishedUrl ? (
                    <a
                      href={site.primaryPublishedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                    >
                      View live
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {tenants.length === 0 ? (
        <WorkspaceEmptyState
          title="No client tenants are linked to this organization yet"
          description="This org has no sub-accounts in the control plane, so there is no builder workspace to surface."
        />
      ) : (
        <section className="grid gap-4 xl:grid-cols-2">
          {tenants.map((tenant) => {
            const leadSite = tenant.sites[0];

            return (
              <article key={tenant.id} className="stagger-item rounded-[28px] border border-border bg-card p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-semibold text-foreground">{tenant.name}</h2>
                      <StatusChip tone={tenant.health}>{tenant.health}</StatusChip>
                      <StatusChip tone={tenant.reviewState === "live" ? "healthy" : tenant.reviewState === "empty" ? "critical" : "attention"}>
                        {REVIEW_LABEL[tenant.reviewState]}
                      </StatusChip>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {tenant.slug} · {tenant.plan} · {tenant.complianceTier}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/agency/manage/${tenant.id}`)}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
                  >
                    Open managed view
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-4">
                  <SummaryPill label="Sites" value={tenant.counts.siteCount} />
                  <SummaryPill label="Pages" value={tenant.counts.pageCount} />
                  <SummaryPill label="Live" value={tenant.counts.publishedPageCount} />
                  <SummaryPill label="Drafts" value={tenant.counts.draftPageCount} />
                </div>

                {tenant.healthNotes.length > 0 ? (
                  <div className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${HEALTH_TONE[tenant.health]}`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4" />
                      <div className="space-y-1">
                        {tenant.healthNotes.map((note) => (
                          <p key={note}>{note}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="mt-5 rounded-2xl border border-border bg-background p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Lead site</p>
                      <p className="text-sm text-muted-foreground">
                        {leadSite ? `${leadSite.name} · ${leadSite.counts.pageCount} pages` : "No site records yet"}
                      </p>
                    </div>
                    {leadSite?.primaryPublishedUrl ? (
                      <a
                        href={leadSite.primaryPublishedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
                      >
                        View live
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

function computeHealthScore(site: AgencyBuilderWorkspaceSiteSummary): number {
  let score = 100;
  if (site.health === "attention") score -= 20;
  if (site.health === "critical") score -= 40;
  if (!site.primaryPublishedUrl) score -= 15;
  if (!site.homePageTitle) score -= 15;
  score -= Math.min(site.healthNotes.length * 5, 20);
  return Math.max(0, score);
}

function formatRelativeDate(value: string): string {
  return formatDate(value);
}

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string;
  detail: string;
  icon: typeof Building2;
}) {
  return (
    <article className="stagger-item rounded-[28px] border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-foreground">{value}</p>
      <p className="mt-2 text-sm text-muted-foreground">{detail}</p>
    </article>
  );
}

function SummaryPill({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stagger-item rounded-2xl border border-border bg-card px-4 py-3">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-3">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function StatusChip({
  tone,
  children,
}: {
  tone: "healthy" | "attention" | "critical";
  children: string;
}) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${HEALTH_TONE[tone]}`}>
      {children}
    </span>
  );
}

function WorkspaceEmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex min-h-[320px] items-center justify-center rounded-[28px] border border-dashed border-border bg-card p-10 text-center">
      <div className="max-w-lg space-y-3">
        <Loader2 className="mx-auto h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        <p className="text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
